// Roster authoring form (nursing-ops sprint, Epic 7 / E7-FE): lets a nursing
// manager create NurseWardRoster row(s) — a single day, or a date range that
// the backend expands into one row per calendar date. The overtime cap (a
// nurse's 3rd "overtime" row in a rolling 7-day window) and unique-conflict
// clashes are rejected per-date by the backend and come back in
// skipped/errors — this form surfaces both, not just the top-level status.
import React, { useState } from "react";
import {
  Alert, Box, Button, Grid, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import { addWardRoster } from "../api/nurseApi";
import { useShiftSchedule } from "../hooks";

// Canonical enums (lowercase values match backend VALID_SHIFTS / VALID_STATUSES
// in main/ipd/nurse_dashboard_common.py + manage_nurse_ward_roster.py).
const STATUS_OPTIONS = [
  { value: "on_duty", label: "On Duty" },
  { value: "overtime", label: "Overtime" },
  { value: "leave", label: "Leave" },
  { value: "na", label: "N/A" },
];

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// nurseIds is a multi-select (nursing-ops-2 #05): one backend call per nurse,
// each expanding the date range server-side; outcomes are aggregated per row.
const EMPTY = { wardCode: "", nurseIds: [], shift: "", status: "on_duty", startDate: todayStr(), endDate: "" };

export default function RosterAuthoringForm({ wards = [], nurses, onCreated }) {
  const { schedule } = useShiftSchedule();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null); // { created, skipped, errors, message }

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // A ward is a room category and nothing else — a shared ICU is one ward
  // staffed by one team, not one ward per department. The Department -> Ward
  // cascade this form used to carry is gone with it.
  const canSubmit =
    form.wardCode && form.nurseIds.length > 0 && form.shift && form.status && form.startDate && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setOutcome(null);

    // One call per selected nurse; the backend expands the date range into one
    // row per date and reports data/skipped/errors per date. Aggregate across
    // nurses so a cap rejection for one nurse never hides the others' success.
    const allSkipped = [];
    const allErrors = [];
    let createdCount = 0;
    for (const nurseId of form.nurseIds) {
      const nurse = nurses.find((n) => n.nurse_id === nurseId);
      const payload = {
        nurse_id: nurseId,
        nurse_name: nurse?.name,
        ward_code: form.wardCode, // RoomCategory.code (the "ward")
        shift: form.shift,
        status: form.status,
        roster_date: form.startDate,
      };
      if (form.endDate && form.endDate !== form.startDate) payload.end_date = form.endDate;
      try {
        const res = await addWardRoster(payload);
        createdCount += Array.isArray(res?.data) ? res.data.length : 0;
        (res?.skipped || []).forEach((s) => allSkipped.push({ ...s, nurse: nurse?.name || nurseId }));
        (res?.errors || []).forEach((e) => allErrors.push({ ...e, nurse: nurse?.name || nurseId }));
        if (res?.status !== "Success" && (res?.errors || []).length === 0) {
          allErrors.push({ roster_date: form.startDate, error: res?.message || "Request failed", nurse: nurse?.name || nurseId });
        }
      } catch (err) {
        allErrors.push({
          roster_date: form.startDate,
          error: err?.response?.data?.message || "Request failed",
          nurse: nurse?.name || nurseId,
        });
      }
    }

    setOutcome({ created: createdCount, skipped: allSkipped, errors: allErrors, message: null });

    if (allErrors.length > 0) {
      toast.error(`${createdCount} created, ${allErrors.length} rejected`);
    } else if (allSkipped.length > 0) {
      toast.warn(`${createdCount} created, ${allSkipped.length} skipped`);
    } else if (createdCount > 0) {
      toast.success(`${createdCount} roster row(s) created`);
    } else {
      toast.warn("No roster rows were created");
    }

    if (createdCount > 0) {
      setForm(EMPTY);
      onCreated?.();
    }
    setSubmitting(false);
  };

  return (
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
      <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Add Roster Entry</Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            select fullWidth size="small" label="Ward" value={form.wardCode}
            onChange={(e) => set({ wardCode: e.target.value })}
          >
            <MenuItem value="">Select ward</MenuItem>
            {wards.map((w) => (<MenuItem key={w.code} value={w.code}>{w.name}</MenuItem>))}
            {wards.length === 0 && (
              <MenuItem value="" disabled>No wards configured</MenuItem>
            )}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            select fullWidth size="small" label="Nurse(s)" value={form.nurseIds}
            onChange={(e) => set({ nurseIds: typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value })}
            SelectProps={{
              multiple: true,
              renderValue: (selected) =>
                selected.length <= 2
                  ? selected.map((id) => nurses.find((n) => n.nurse_id === id)?.name || id).join(", ")
                  : `${selected.length} nurses`,
            }}
            helperText="Multi-select for batch assignment"
          >
            {nurses.map((n) => (<MenuItem key={n.nurse_id} value={n.nurse_id}>{n.name}</MenuItem>))}
            {nurses.length === 0 && <MenuItem value="" disabled>No nurses available</MenuItem>}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            type="date" fullWidth size="small" label="From date" value={form.startDate}
            onChange={(e) => set({ startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            type="date" fullWidth size="small" label="To date (optional)" value={form.endDate}
            onChange={(e) => set({ endDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: form.startDate || undefined }}
            helperText="One row per date"
          />
        </Grid>
        <Grid item xs={6} sm={3} md={1}>
          <TextField
            select fullWidth size="small" label="Shift" value={form.shift}
            onChange={(e) => set({ shift: e.target.value })}
          >
            {schedule.map((s) => (<MenuItem key={s.code} value={s.code}>{s.label}</MenuItem>))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3} md={1}>
          <TextField
            select fullWidth size="small" label="Status" value={form.status}
            onChange={(e) => set({ status: e.target.value })}
          >
            {STATUS_OPTIONS.map((s) => (<MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>))}
          </TextField>
        </Grid>
      </Grid>
      <Stack direction="row" sx={{ mt: 1.5 }}>
        <Button variant="contained" disabled={!canSubmit} onClick={submit}>
          {submitting ? "Saving..." : "Add to Roster"}
        </Button>
      </Stack>

      {outcome && (outcome.skipped.length > 0 || outcome.errors.length > 0) && (
        <Box sx={{ mt: 1.5 }}>
          {outcome.errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {outcome.errors.length} row(s) rejected:
              </Typography>
              {outcome.errors.map((e, i) => (
                <Typography key={i} variant="caption" component="div">
                  {e.nurse ? `${e.nurse} — ` : ""}{e.roster_date}: {e.error}
                </Typography>
              ))}
            </Alert>
          )}
          {outcome.skipped.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {outcome.skipped.length} row(s) skipped:
              </Typography>
              {outcome.skipped.map((s, i) => (
                <Typography key={i} variant="caption" component="div">
                  {s.nurse ? `${s.nurse} — ` : ""}{s.roster_date}: {s.reason}
                </Typography>
              ))}
            </Alert>
          )}
        </Box>
      )}
    </Paper>
  );
}
